'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { AdminMenu, Section } from './admin-menu';

type Perms = Record<string, { view?: boolean; edit?: boolean }>;

export interface AdminPermission {
  userRole: string;
  managerPerms: Perms;
  // 섹션별 조회/편집 가능 여부(관리자·최고관리자는 전부 true, 매니저는 권한 기반)
  canView: (section: string) => boolean;
  canEdit: (section: string) => boolean;
  // 메뉴 접근 가능 여부(access 모드 반영)
  canAccessMenu: (menu: AdminMenu) => boolean;
}

// 권한 판정 단일 소스. AdminShell(메뉴/리다이렉트)과 페이지(컨텍스트) 모두 이 함수를 사용.
export function createPermission(userRole: string, managerPerms: Perms): AdminPermission {
  const isManager = userRole === 'MANAGER';
  const isAdminOrSuper = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';
  const isSuper = userRole === 'SUPER_ADMIN';

  const canView = (section: string) => {
    if (isAdminOrSuper) return true;
    if (!isManager) return false;
    if (section === 'permitImages') return true; // 도면관리는 매니저 기본 허용
    return !!managerPerms[section]?.view;
  };
  const canEdit = (section: string) => {
    if (isAdminOrSuper) return true;
    if (!isManager) return false;
    if (section === 'permitImages') return true;
    return !!managerPerms[section]?.edit;
  };
  const canAccessMenu = (menu: AdminMenu) => {
    if (menu.access === 'superadmin') return isSuper;
    if (menu.access === 'admin') return isAdminOrSuper;
    return canView(menu.section);
  };

  return { userRole, managerPerms, canView, canEdit, canAccessMenu };
}

const AdminPermissionContext = createContext<AdminPermission>(createPermission('', {}));

export function AdminPermissionProvider({
  userRole,
  managerPerms,
  children,
}: {
  userRole: string;
  managerPerms: Perms;
  children: ReactNode;
}) {
  return (
    <AdminPermissionContext.Provider value={createPermission(userRole, managerPerms)}>
      {children}
    </AdminPermissionContext.Provider>
  );
}

export const useAdminPermission = () => useContext(AdminPermissionContext);

// 페이지 편집/조회 컨트롤 게이팅 표준 헬퍼.
// <Can section="faq" edit>…</Can> → 편집 권한 있을 때만 / <Can section="faq">…</Can> → 조회 권한 있을 때만
export function Can({
  section,
  edit = false,
  children,
}: {
  section: Section;
  edit?: boolean;
  children: ReactNode;
}) {
  const perm = useAdminPermission();
  const allowed = edit ? perm.canEdit(section) : perm.canView(section);
  return allowed ? <>{children}</> : null;
}
